import { apiRequest } from './axiosConfig';

// マジックリサイズカテゴリ型定義
export interface MagicResizeCategory {
  id: number;
  name: string;
  display_order: number;
  is_active: boolean;
  presets_count?: number;
  presets?: MagicResizePreset[];
  created_at?: string;
  updated_at?: string;
}

export interface MagicResizeCategoryCreateRequest {
  name: string;
  display_order?: number;
  is_active?: boolean;
}

export interface MagicResizeCategoryUpdateRequest {
  name?: string;
  display_order?: number;
  is_active?: boolean;
}

// マジックリサイズプリセット型定義
export interface MagicResizePreset {
  id: number;
  category_id: number;
  name: string;
  width: number;
  height: number;
  prompt?: string | null;
  display_order: number;
  is_active: boolean;
  category?: MagicResizeCategory;
  created_at?: string;
  updated_at?: string;
}

export interface MagicResizePresetCreateRequest {
  category_id: number;
  name: string;
  width: number;
  height: number;
  prompt?: string | null;
  display_order?: number;
  is_active?: boolean;
}

export interface MagicResizePresetUpdateRequest {
  category_id?: number;
  name?: string;
  width?: number;
  height?: number;
  prompt?: string | null;
  display_order?: number;
  is_active?: boolean;
}

interface CategoriesResponse {
  success: boolean;
  data: MagicResizeCategory[];
}

interface CategoryResponse {
  success: boolean;
  data: MagicResizeCategory;
  message?: string;
}

interface CategoryDeleteResponse {
  success: boolean;
  message: string;
}

interface PresetResponse {
  success: boolean;
  data: MagicResizePreset;
  message?: string;
}

interface PresetDeleteResponse {
  success: boolean;
  message: string;
}

/**
 * 管理者用マジックリサイズ APIサービス
 */
const adminMagicResizeService = {
  /**
   * カテゴリ一覧取得（プリセット含む）
   */
  async getCategories(): Promise<MagicResizeCategory[]> {
    const response = await apiRequest.get<CategoriesResponse>('admin/magic-resize/categories');
    return response.data;
  },

  /**
   * カテゴリ詳細取得
   */
  async getCategory(id: number): Promise<MagicResizeCategory> {
    const response = await apiRequest.get<CategoryResponse>(`admin/magic-resize/categories/${id}`);
    return response.data;
  },

  /**
   * カテゴリ作成
   */
  async createCategory(data: MagicResizeCategoryCreateRequest): Promise<MagicResizeCategory> {
    const response = await apiRequest.post<CategoryResponse>('admin/magic-resize/categories', data);
    return response.data;
  },

  /**
   * カテゴリ更新
   */
  async updateCategory(id: number, data: MagicResizeCategoryUpdateRequest): Promise<MagicResizeCategory> {
    const response = await apiRequest.put<CategoryResponse>(`admin/magic-resize/categories/${id}`, data);
    return response.data;
  },

  /**
   * カテゴリ削除
   */
  async deleteCategory(id: number): Promise<void> {
    await apiRequest.delete<CategoryDeleteResponse>(`admin/magic-resize/categories/${id}`);
  },

  /**
   * カテゴリの有効/無効切り替え
   */
  async toggleCategory(id: number): Promise<MagicResizeCategory> {
    const response = await apiRequest.put<CategoryResponse>(`admin/magic-resize/categories/${id}/toggle`);
    return response.data;
  },

  /**
   * プリセット作成
   */
  async createPreset(data: MagicResizePresetCreateRequest): Promise<MagicResizePreset> {
    const response = await apiRequest.post<PresetResponse>('admin/magic-resize/presets', data);
    return response.data;
  },

  /**
   * プリセット更新
   */
  async updatePreset(id: number, data: MagicResizePresetUpdateRequest): Promise<MagicResizePreset> {
    const response = await apiRequest.put<PresetResponse>(`admin/magic-resize/presets/${id}`, data);
    return response.data;
  },

  /**
   * プリセット削除
   */
  async deletePreset(id: number): Promise<void> {
    await apiRequest.delete<PresetDeleteResponse>(`admin/magic-resize/presets/${id}`);
  },

  /**
   * プリセットの有効/無効切り替え
   */
  async togglePreset(id: number): Promise<MagicResizePreset> {
    const response = await apiRequest.put<PresetResponse>(`admin/magic-resize/presets/${id}/toggle`);
    return response.data;
  },
};

export default adminMagicResizeService;
